import InputV2 from "@appComponents/InputV2";
import { EditModal } from "../components/EditModal";
import { ProfilePictureModal } from "../components/Modals/AvatarModal";
import { EmailModal } from "../components/Modals/EmailModal";
import SelectV2 from "@appComponents/SelectV2";
import { useMemo, useState } from "react";
import ProfileService from "@services/Profile.service";
import { Section } from "../components/Section";
import { editFieldModalParams, getProfileSections } from "../const";
import { updateNestedObjectMulti } from "../utils";
import React from "react";
import { CloseAccountModal } from "../components/Modals/CloseAccountModal";
import { useAccountPageContext } from "../AcountPage.context";

const paths = require("../paths");

type ProfileProps = {
  fetchProfileData: () => void;
  setActiveTab: (section?: string) => void;
};

export const Profile = ({ fetchProfileData, setActiveTab }: ProfileProps) => {
  const [editField, setEditField] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isProfilePictureModalOpen, setIsProfilePictureModalOpen] =
    useState(false);
  const [isEditEmailModalOpen, setIsEditEmailModalOpen] = useState(false);
  const [isCloseAccountModalOpen, setIsCloseAccountModalOpen] = useState(false);
  const [displaySuccessChangeEmailMsg, setDisplaySuccessChangeEmailMsg] =
    useState(false);

  const { state, is2FAEnabled } = useAccountPageContext();

  const editModalData = useMemo(() => {
    return editFieldModalParams[editField];
  }, [editField]);

  const handleOpenEditModal = (fieldName: string, initialValue?: string) => {
    if (fieldName === "profile_picture") {
      setIsProfilePictureModalOpen(true);
      return;
    }

    if (fieldName === "email") {
      setIsEditEmailModalOpen(true);
      return;
    }

    if (initialValue) {
      setInputValue(initialValue);
    }

    setEditField(fieldName);
  };

  const handleCloseEditModal = () => {
    setEditField("");
    setInputValue("");
  };

  const handleOpenCloseAccountModal = () => {
    setIsCloseAccountModalOpen(true);
  };

  const handleCloseEmailEditModal = () => {
    setIsEditEmailModalOpen(false);
  };

  const handleUpdateProfilePicture = ({
    imageId,
    imageUrl,
  }: {
    imageId: string;
    imageUrl: string;
  }) => {
    const imageIdPath = paths.profileDataProfile("image_id");
    const imageUrlPath = paths.profileDataProfile("image_url");

    const updatedProfileData = updateNestedObjectMulti(state.profileData, [
      { path: imageIdPath, value: imageId },
      { path: imageUrlPath, value: imageUrl },
    ]);

    ProfileService.updateProfileData(updatedProfileData).then(() => {
      fetchProfileData();
      setIsProfilePictureModalOpen(false);
      handleCloseEditModal();
    });
  };

  const handleUpdateEmail = (
    email: string,
    password: string,
    otpCode: string
  ) => {
    const data = {
      email,
      otp_code: otpCode,
      old_password: password,
    };

    ProfileService.changeEmail({
      data,
      validation: {},
    }).then(() => {
      setDisplaySuccessChangeEmailMsg(true);
    });
  };

  const handleUpdateProfileData = () => {
    const updatedProfileData = updateNestedObjectMulti(state.profileData, [
      {
        path: paths.profileDataProfile(editModalData?.fieldName),
        value: inputValue,
      },
    ]);

    ProfileService.updateProfileData(updatedProfileData).then(() => {
      fetchProfileData();
      handleCloseEditModal();
    });
  };

  const profileTabSections = useMemo(
    () =>
      getProfileSections(
        state,
        setActiveTab,
        handleOpenEditModal,
        handleOpenCloseAccountModal,
        is2FAEnabled
      ),
    [state]
  );

  return (
    <>
      {profileTabSections.map((section) => (
        <Section {...section} />
      ))}
      <ProfilePictureModal
        isOpen={isProfilePictureModalOpen}
        onClose={() => setIsProfilePictureModalOpen(false)}
        onUploadFile={handleUpdateProfilePicture}
      />

      <EmailModal
        isOpen={isEditEmailModalOpen}
        currentEmail={state?.profileData?.verification?.email}
        shouldDisplaySuccessContent={displaySuccessChangeEmailMsg}
        onClose={handleCloseEmailEditModal}
        onSave={handleUpdateEmail}
      />

      <EditModal
        open={!!editField}
        title={editModalData?.title}
        description={editModalData?.description}
        onClose={handleCloseEditModal}
        onSave={handleUpdateProfileData}
        isOnSaveDisabled={!inputValue}
      >
        {editModalData?.fieldType !== "select" && (
          <InputV2
            label={editModalData?.fieldLabel ?? editModalData?.title}
            placeholder="Type here"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        )}

        {editModalData?.fieldType === "select" && (
          <SelectV2
            label={editModalData?.fieldLabel ?? editModalData?.title}
            options={editModalData?.fieldOptions}
            value={editModalData?.fieldOptions?.find(
              (opt) => opt.value === inputValue
            )}
            onChange={(selectedOpt) => setInputValue(selectedOpt.value)}
          />
        )}
      </EditModal>

      <CloseAccountModal
        open={isCloseAccountModalOpen}
        onClose={() => setIsCloseAccountModalOpen(false)}
      />
    </>
  );
};
